import { orderApi } from "@api/acquisition";
import { getSearchTotal } from "@api/utils";
import { NewButton } from "@components/backoffice/buttons/NewButton";
import { ExportReactSearchKitResults } from "@components/backoffice/ExportSearchResults";
import { QueryBuildHelper } from "@components/SearchBar/QueryBuildHelper";
import {
  invenioConfig,
  setReactSearchKitDefaultSortingOnEmptyQueryString,
  setReactSearchKitInitialQueryState,
  setReactSearchKitUrlHandler,
} from "@config";
import history from "@history";
import SearchAggregationsCards from "@modules/SearchControls/SearchAggregationsCards";
import { SearchControls } from "@modules/SearchControls/SearchControls";
import { SearchControlsOverridesMap } from "@modules/SearchControls/SearchControlsOverrides";
import SearchFooter from "@modules/SearchControls/SearchFooter";
import { OrderListEntry } from "@pages/backoffice/Acquisition/Order/OrderSearch/OrderList";
import { AcquisitionRoutes } from "@routes/urls";
import React, { Component } from "react";
import { OverridableContext } from "react-overridable";
import {
  EmptyResults,
  Error,
  InvenioSearchApi,
  ReactSearchKit,
  ResultsList,
  ResultsLoader,
  SearchBar,
  withState,
} from "react-searchkit";
import { Container, Grid, Header, List } from "semantic-ui-react";
import ClearFiltersButton from "@modules/SearchControls/ClearFiltersButton";
import SessionFiltersPersistence from "@modules/SearchControls/SessionFiltersPersistence";
import Qs from "qs";
import { generateExportHTML } from "@components/HtmlExportTemplate/htmlExportTemplate";
import { DatePicker } from "@components/DatePicker";

class OrderResponseSerializer {
  serialize(results) {
    const hits = results.hits.hits.map((hit) =>
      orderApi.serializer.fromJSON(hit)
    );
    return {
      aggregations: results.aggregations || {},
      hits: hits,
      total: getSearchTotal(results.hits),
    };
  }
}

const DateRangeFilter = withState(
  ({
    updateQueryState,
    currentQueryState,
    fromDate,
    toDate,
    onFromDateChange,
    onToDateChange,
  }) => {
    const handleFromDateChange = (value) => {
      onFromDateChange(value);
      updateDateFilter(value, toDate);
    };

    const handleToDateChange = (value) => {
      onToDateChange(value);
      updateDateFilter(fromDate, value);
    };

    const updateDateFilter = (from, to) => {
      let query = currentQueryState.queryString || "";

      // Remove any existing received_date range filters from the query string
      query = query.replace(/\s*AND\s*received_date:\[[^\]]+\]/g, "");
      query = query.replace(/^received_date:\[[^\]]+\]\s*AND\s*/, "");
      query = query.replace(/^received_date:\[[^\]]+\]$/, "");
      query = query.trim();

      const filters = [];
      if (from) filters.push(`received_date:[${from} TO *]`);
      if (to) filters.push(`received_date:[* TO ${to}]`);
      if (filters.length)
        query = query
          ? `${query} AND ${filters.join(" AND ")}`
          : filters.join(" AND ");

      updateQueryState({ ...currentQueryState, queryString: query, page: 1 });
    };

    return (
      <Container fluid className="spaced">
        <Header as="h4">
          Filter by Received Date Range
          <Header.Subheader>
            Choose date range to filter orders by received date
          </Header.Subheader>
        </Header>
        <List horizontal>
          <List.Item>
            <List.Content>
              <DatePicker
                maxDate={toDate}
                placeholder="From Date"
                handleDateChange={handleFromDateChange}
                name="fromDate"
                id="fromDate"
                label="From Date"
                value={fromDate}
              />
            </List.Content>
          </List.Item>
          <List.Item>
            <List.Content>
              <DatePicker
                minDate={fromDate}
                placeholder="To Date"
                handleDateChange={handleToDateChange}
                name="toDate"
                id="toDate"
                label="To Date"
                value={toDate}
              />
            </List.Content>
          </List.Item>
        </List>
      </Container>
    );
  }
);

export class OrderSearch extends Component {
  modelName = "ACQ_ORDERS";

  constructor(props) {
    super(props);
    this.state = { fromDate: "", toDate: "" };
  }

  searchApi = new InvenioSearchApi({
    axios: { url: orderApi.searchBaseURL, withCredentials: true },
    invenio: { responseSerializer: OrderResponseSerializer },
  });

  getRequestSerializer = () => {
    const serializer = this.searchApi.requestSerializer;
    if (!serializer || typeof serializer.serialize !== "function") {
      throw new Error("RequestSerializer misconfigured in OrderSearch");
    }
    return serializer;
  };

  parseCsvData = (csvText) => {
    const lines = csvText.trim().split("\n");
    const headers = lines[0].split(",").map((h) => h.replace(/"/g, "").trim());

    const dataRows = lines.slice(1).map((line) => {
      const row = [];
      let current = "",
        inQuotes = false;
      for (let char of line) {
        if (char === '"') inQuotes = !inQuotes;
        else if (char === "," && !inQuotes) {
          row.push(current.trim());
          current = "";
        } else current += char;
      }
      row.push(current.trim());
      return row;
    });

    return { headers, dataRows };
  };

  generateTableContent = ({ headers, dataRows }) => {
    const tableHeaders = headers
      .map(
        (header) =>
          `<th style="padding: 8px; border: 1px solid #ddd; background-color: #225745; font-weight: bold;">${header}</th>`
      )
      .join("");

    const tableRows = dataRows
      .map((row) => {
        const cells = row.map((cell, i) => {
          let value = cell || "";
          const header = headers[i].toLowerCase();

          // Clean up authors field - remove brackets and quotes
          if (header.includes("author")) {
            value = value.replace(/[[\]"]/g, "").trim();
          }

          // Clean up title field
          if (header.includes("title")) {
            value = value.replace(/^"|"$/g, "").trim();
          }

          // Handle date fields
          if (header.includes("date")) {
            if (value && value.includes("T")) {
              value = value.split("T")[0]; // Just show date part
            }
          }

          return `<td style="padding:8px;border:1px solid #ddd;">${value}</td>`;
        });
        return `<tr>${cells.join("")}</tr>`;
      })
      .join("");

    return { tableHeaders, tableRows };
  };

  // Helper method to flatten nested order data
  flattenOrderData = (hits) => {
    return hits.map((hit) => {
      const metadata = hit.metadata || {};
      const document = metadata.document || {};

      return {
        Status: metadata.status,
        "Received Date": metadata.received_date,
        Medium: metadata.medium,
        Title: document.title,
        Author: document.authors,
      };
    });
  };

  // Helper method to convert flattened data to CSV
  convertToCSV = (data) => {
    if (!data.length) return "";

    const headers = Object.keys(data[0]);
    const csvHeaders = headers.join(",");

    const csvRows = data.map((row) => {
      return headers
        .map((header) => {
          let value = row[header] || "";
          // Escape quotes and wrap in quotes if contains comma
          if (
            typeof value === "string" &&
            (value.includes(",") || value.includes('"'))
          ) {
            value = `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        })
        .join(",");
    });

    return [csvHeaders, ...csvRows].join("\n");
  };

  handleExportClick = async (format, size, currentQueryState) => {
    const serializer = this.getRequestSerializer();
    const queryString = serializer.serialize({ ...currentQueryState, size });
    const params = Qs.parse(queryString);
    params.page = 1;

    if (format === "pdf") {
      try {
        // Use JSON approach for reliable data extraction
        delete params.format; // Remove format to get JSON
        const jsonUrl = `${orderApi.searchBaseURL}?${Qs.stringify(params, {
          arrayFormat: "repeat",
          encode: false,
        })}`;

        const jsonRes = await fetch(jsonUrl, { credentials: "include" });
        if (!jsonRes.ok) throw new Error(`HTTP error ${jsonRes.status}`);

        const jsonData = await jsonRes.json();

        // Handle different possible JSON structures
        let hits = [];
        if (jsonData.hits && Array.isArray(jsonData.hits.hits)) {
          hits = jsonData.hits.hits;
        } else if (jsonData.hits && Array.isArray(jsonData.hits)) {
          hits = jsonData.hits;
        } else if (Array.isArray(jsonData)) {
          hits = jsonData;
        } else {
          throw new Error("Unable to find hits array in JSON response");
        }

        // Manually flatten the data
        const flattenedData = this.flattenOrderData(hits);

        if (!flattenedData.length) {
          throw new Error("No data to export");
        }

        const csvContent = this.convertToCSV(flattenedData);
        const parsed = this.parseCsvData(csvContent);

        const { tableHeaders, tableRows } = this.generateTableContent(parsed);

        const html = generateExportHTML({
          reportTitle: "Order Records Export Report",
          generatedDate: new Date().toLocaleString(),
          tableHeaders,
          tableRows,
          totalRecords: flattenedData.length,
          showUniversityLogo: true,
          showLrcLogo: true,
          fallbackLogoText: "Library Resource Center",
        });

        const blob = new Blob([html], { type: "text/html;charset=utf-8" });
        const urlBlob = URL.createObjectURL(blob);
        const w = window.open(urlBlob, "_blank");
        if (w) w.onload = () => setTimeout(() => w.focus(), 500);
        setTimeout(() => URL.revokeObjectURL(urlBlob), 2000);
      } catch (error) {
        alert(
          "Error generating PDF export. Please try again or contact support."
        );
      }
      return;
    }

    // For non-PDF exports (CSV, Excel)
    try {
      params.format = format;
      const url = `${orderApi.searchBaseURL}?${Qs.stringify(params, {
        arrayFormat: "repeat",
        encode: false,
      })}`;
      window.open(url, "_blank");
    } catch (err) {
      alert(
        `Error generating ${format.toUpperCase()} export. Please try again.`
      );
    }
  };

  handleFromDateChange = (val) => this.setState({ fromDate: val });
  handleToDateChange = (val) => this.setState({ toDate: val });

  render() {
    const { fromDate, toDate } = this.state;
    const helperFields = [
      { name: "provider", field: "provider.name", defaultValue: '"Dolor"' },
      {
        name: "recipient",
        field: "order_lines.recipient",
        defaultValue: "PATRON",
      },
      { name: "patron id", field: "order_lines.patron_pid", defaultValue: "1" },
    ];

    const initialState = setReactSearchKitInitialQueryState(this.modelName);
    const defaultSorting = setReactSearchKitDefaultSortingOnEmptyQueryString(
      this.modelName
    );
    const urlHandler = setReactSearchKitUrlHandler(this.modelName);

    return (
      <>
        <Header as="h2">New Acquisition</Header>
        <OverridableContext.Provider value={{ ...SearchControlsOverridesMap }}>
          <ReactSearchKit
            searchApi={this.searchApi}
            history={history}
            urlHandlerApi={urlHandler}
            initialQueryState={initialState}
            defaultSortingOnEmptyQueryString={defaultSorting}
          >
            <>
              <SessionFiltersPersistence moduleIdentifier="orders" />
              <Container fluid className="spaced">
                <SearchBar
                  placeholder="Search for orders..."
                  {...invenioConfig.APP.SEARCH_BAR_PROPS}
                />
                <QueryBuildHelper fields={helperFields} />
              </Container>

              <DateRangeFilter
                fromDate={fromDate}
                toDate={toDate}
                onFromDateChange={this.handleFromDateChange}
                onToDateChange={this.handleToDateChange}
              />

              <Container fluid className="bo-search-body">
                <Grid>
                  <Grid.Row columns={2}>
                    <ResultsLoader>
                      <Grid.Column width={3} className="search-aggregations">
                        <Grid.Column width={16}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "1rem",
                            }}
                          >
                            <Header
                              as="h4"
                              content="Filter by"
                              style={{ margin: 0 }}
                            />
                            <ClearFiltersButton module-identifier="orders" />
                          </div>
                        </Grid.Column>
                        <SearchAggregationsCards modelName={this.modelName} />
                      </Grid.Column>
                      <Grid.Column width={13}>
                        <Grid columns={2}>
                          <Grid.Column width={8}>
                            <NewButton
                              text="Add Record"
                              to={AcquisitionRoutes.orderCreate}
                            />
                          </Grid.Column>
                          <Grid.Column width={8} textAlign="right">
                            <ExportReactSearchKitResults
                              exportBaseUrl={orderApi.searchBaseURL}
                              onExportClick={this.handleExportClick}
                              supportedFormats={["csv", "pdf"]}
                            />
                          </Grid.Column>
                        </Grid>
                        <EmptyResults
                          extraContent={
                            <NewButton
                              text="Add Record"
                              to={AcquisitionRoutes.orderCreate}
                            />
                          }
                        />
                        <Error />
                        <SearchControls
                          modelName={this.modelName}
                          withLayoutSwitcher={false}
                        />
                        <ResultsList ListEntryElement={OrderListEntry} />
                        <SearchFooter />
                      </Grid.Column>
                    </ResultsLoader>
                  </Grid.Row>
                </Grid>
              </Container>
            </>
          </ReactSearchKit>
        </OverridableContext.Provider>
      </>
    );
  }
}
